import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import httpService from "../services/httpService";
import posthog from 'posthog-js';
import { useAuthStore } from "../store/store";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { FaCheck } from "react-icons/fa";
import "../styles/Join.scss";

const INSIDE_THE_CLUB = [
    "Several private insights from each episode, yours to try at your own pace",
    "Private by default, you decide if and when to share anything",
    "A discussion thread for each episode, join in whenever it suits you",
];

const PodsyMark = () => (
    <svg viewBox="0 0 160 100" xmlns="http://www.w3.org/2000/svg" focusable="false">
        <rect x="4" y="4" width="152" height="92" rx="14" fill="#0a0f14" />
        <circle cx="58" cy="50" r="17" stroke="#f7f7f5" strokeWidth="6" fill="none" />
        <circle cx="108" cy="50" r="12" fill="#e67700" />
        <circle cx="108" cy="50" r="2.2" fill="#f7f7f5" />
        <circle cx="58" cy="50" r="3.4" fill="#e67700" />
    </svg>
);

const Join = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token } = useAuthStore();

    const [code, setCode] = useState(searchParams.get("code") || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clubPreview, setClubPreview] = useState(null); // shown after unauthenticated redeem

    // Auto-submit if code came from query param
    useEffect(() => {
        const paramCode = searchParams.get("code");
        if (paramCode) handleRedeem(null, paramCode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRedeem = async (e, overrideCode) => {
        if (e) e.preventDefault();
        const codeToSend = (overrideCode ?? code).trim();
        if (!codeToSend) return;

        setLoading(true);
        setError(null);
        setClubPreview(null);

        try {
            const res = await httpService.post("invite/redeem", { code: codeToSend });
            const data = res.data.data;

            posthog.capture('invite_code_redeemed', {
                deferred: !!data?.deferred,
                requires_signup: !!data?.signup_token,
                club_guid: data?.podcast_club?.guid ?? null,
            });

            if (data?.deferred) {
                // Logged-in user with incomplete profile — auto-register as listener so the server
                // redeems the stored signup_token and subscribes them to the club
                await httpService.post("dash/register-demographics", { role: 'listener' });
                navigate('/listener-clubs');
                return;
            }

            if (data?.signup_token) {
                // Unauthenticated — store token and show club preview
                sessionStorage.setItem("signup_token", data.signup_token);
                sessionStorage.setItem("signup_club", JSON.stringify(data.podcast_club));
                setClubPreview(data.podcast_club);
                return;
            }

            if (data?.podcast_club) {
                // Logged-in listener — already subscribed, navigate to club
                navigate(`/listener-podcast-club/${data.podcast_club.guid}`);
                return;
            }
        } catch (err) {
            posthog.captureException(err, { tags: { context: 'invite_redeem' } });
            setError(err.response?.data?.message || "Something went wrong. Please check the code and try again.");
        } finally {
            setLoading(false);
        }
    };

    const clubImage = clubPreview ? (clubPreview.tall_image || clubPreview.wide_image || clubPreview.super_wide_image || "/img/club-defaults/tall.jpg") : null;
    const episodeCount = clubPreview?.episode_count || 6;

    const resetPreview = () => {
        setClubPreview(null);
        sessionStorage.removeItem("signup_token");
        sessionStorage.removeItem("signup_club");
    };

    return (
        <div className="join-page">
            <span className="join-page__mark" aria-hidden="true"><PodsyMark /></span>

            <div className="join-page__wrap">
                <div className="join-card">
                    {clubPreview ? (
                        <>
                            <div className="join-card__meta">
                                <span className="join-card__kicker">Podcast Club</span>
                                <span className="join-card__format">{episodeCount}-episode series</span>
                            </div>

                            <div className="join-card__identity">
                                <img className="join-card__thumb" src={clubImage} alt={`${clubPreview.title} cover art`} />
                                <div>
                                    <h1 className="join-card__title">{clubPreview.title}</h1>
                                    {clubPreview.host_name && (
                                        <p className="join-card__host">Hosted by <strong>{clubPreview.host_name}</strong></p>
                                    )}
                                </div>
                            </div>

                            <div className="join-card__hook">
                                <p>
                                    <strong>{episodeCount}</strong> episodes, released one per week. Once an episode is
                                    live it stays open for good, so you can catch up any time and you're never behind.
                                </p>
                            </div>

                            <div className="join-card__inside">
                                <h2>Inside the Club</h2>
                                <ul>
                                    {INSIDE_THE_CLUB.map((line) => (
                                        <li key={line}><FaCheck aria-hidden="true" />{line}</li>
                                    ))}
                                </ul>
                            </div>

                            <button type="button" className="join-card__primary" onClick={() => navigate("/signup")}>
                                Get me in
                            </button>
                            <p className="join-card__hint">Takes under a minute.</p>
                            <button type="button" className="join-card__secondary" onClick={() => navigate("/login")}>
                                I already have an account
                            </button>
                            <div className="text-center mt-2">
                                <button type="button" className="join-card__link" onClick={resetPreview}>
                                    Use a different code
                                </button>
                            </div>
                        </>
                    ) : loading && code ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="text-muted small mt-3 mb-0">Checking your invite…</p>
                        </div>
                    ) : (
                        <>
                            <h1 className="join-card__title mb-1">Join a club</h1>
                            <p className="text-muted small mb-4">Enter the invite code you received to get access.</p>

                            {error && <Alert variant="danger">{error}</Alert>}

                            <Form onSubmit={handleRedeem}>
                                <Form.Group className="mb-3" controlId="joinCode">
                                    <Form.Label>Invite code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. LEADER2026"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        disabled={loading}
                                        autoFocus
                                    />
                                </Form.Group>
                                <button type="submit" className="join-card__primary" disabled={!code.trim() || loading}>
                                    {loading ? <><Spinner animation="border" size="sm" className="me-2" />Checking…</> : "Join"}
                                </button>
                            </Form>

                            <div className="text-center mt-3 small text-muted">
                                Already have an account? <Link to="/login">Log in</Link>
                            </div>
                        </>
                    )}

                    <div className="join-card__powered">
                        <PodsyMark />
                        <span>Powered by Podsy</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Join;
